import React, { useState, useEffect } from 'react';
import { 
  User, 
  LogOut, 
  CreditCard, 
  Clock, 
  Award, 
  PenTool, 
  Sparkles, 
  BookOpen, 
  Phone, 
  X, 
  Check, 
  FileText, 
  Video, 
  Download,
  ArrowRight,
  Linkedin,
  Globe
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getSubscriptionInfo, logoutUser } from '../lib/auth';
import { User as UserType } from '../lib/types';
import LinkedInProfileModal from './LinkedInProfileModal';
import { supabase } from '../lib/supabase';

interface UserProfileProps {
  user: UserType;
  onLogout: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
  const [isLinkedInModalOpen, setIsLinkedInModalOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [website, setWebsite] = useState(user.company_website || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const subscriptionInfo = user ? getSubscriptionInfo(user.id) : null;

  const handleWebsiteSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const { error } = await supabase
        .from('users')
        .update({ company_website: website })
        .eq('id', user.id);

      if (error) throw error;

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving website:', error);
      setSaveError('Erreur lors de la sauvegarde du site web');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logoutUser();
    onLogout();
  };

  const handleGenerateClick = () => {
    navigate('/générer');
  };

  const handleCallbackRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate sending email
      console.log(`Sending callback request email to gerald@recruteurs.io for ${phoneNumber}`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSuccess(true);
      setTimeout(() => {
        setIsCallbackModalOpen(false);
        setSuccess(false);
        setPhoneNumber('');
      }, 2000);
    } catch (error) {
      console.error('Error sending callback request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Featured resources data
  const featuredResources = [
    {
      id: '1',
      title: 'Guide complet de rédaction',
      description: 'Apprenez à rédiger des offres d\'emploi qui attirent les meilleurs talents.',
      type: 'Guide',
      icon: FileText,
      color: 'blue',
      url: '/resources/1'
    },
    {
      id: '2',
      title: 'Webinaire : Optimisation LinkedIn',
      description: 'Comment optimiser vos offres pour une meilleure visibilité sur LinkedIn.',
      type: 'Vidéo',
      icon: Video,
      color: 'purple',
      url: '/resources/2'
    },
    {
      id: '3',
      title: 'Templates par secteur',
      description: 'Une collection de modèles d\'offres d\'emploi par secteur d\'activité.',
      type: 'Templates',
      icon: FileText,
      color: 'green',
      url: '/resources/3'
    }
  ];
  
  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-indigo-100 p-3 rounded-full">
              <User className="w-8 h-8 text-indigo-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-semibold">{user.name || user.email}</h2>
              <p className="text-gray-500 text-sm">{user.email}</p>
            </div>
          </div>
          
          <button
            onClick={() => setIsLinkedInModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 border-2 border-[#0A66C2] text-[#0A66C2] rounded-md hover:bg-[#0A66C2] hover:text-white transition-colors"
          >
            <Linkedin className="w-5 h-5" />
            <span>LinkedIn</span>
          </button>
        </div>

        {/* Website Field */}
        <div className="mt-6 border-t pt-6">
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Site web de l'entreprise
            </label>
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <Globe className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://www.entreprise.com"
                  className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleWebsiteSave}
                disabled={isSaving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Enregistrer</span>
                  </>
                )}
              </button>
            </div>
            {saveError && (
              <p className="text-sm text-red-600">{saveError}</p>
            )}
            {saveSuccess && (
              <p className="text-sm text-green-600">Site web sauvegardé avec succès</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link 
          to="/pimper"
          className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-4"
        >
          <div className="bg-purple-100 p-3 rounded-full">
            <Sparkles className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold">Pimper une offre</h3>
            <p className="text-sm text-gray-500">Optimisez vos offres existantes</p>
          </div>
        </Link>

        <button 
          onClick={handleGenerateClick}
          className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-4"
        >
          <div className="bg-blue-100 p-3 rounded-full">
            <PenTool className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold">Générer une offre</h3>
            <p className="text-sm text-gray-500">Créez une nouvelle offre</p>
          </div>
        </button>

        <Link 
          to="/resources"
          className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-4"
        >
          <div className="bg-green-100 p-3 rounded-full">
            <BookOpen className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold">Ressources</h3>
            <p className="text-sm text-gray-500">Guides et bonnes pratiques</p>
          </div>
        </Link>
      </div>

      {/* Resources Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">Ressources recommandées</h3>
          <Link 
            to="/resources" 
            className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm"
          >
            <span>Voir toutes les ressources</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {featuredResources.map(resource => {
            const IconComponent = resource.icon;
            return (
              <Link
                key={resource.id}
                to={resource.url}
                className="group bg-gray-50 p-6 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className={`w-12 h-12 rounded-lg bg-${resource.color}-100 flex items-center justify-center mb-4`}>
                  <IconComponent className={`w-6 h-6 text-${resource.color}-600`} />
                </div>
                <h4 className="font-semibold text-gray-800 mb-2 group-hover:text-indigo-600 transition-colors">
                  {resource.title}
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {resource.description}
                </p>
                <div className="flex items-center text-sm text-gray-500">
                  <span className="bg-white px-2 py-1 rounded">
                    {resource.type}
                  </span>
                  <Download className="w-4 h-4 ml-auto text-gray-400 group-hover:text-indigo-600 transition-colors" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Subscription Info */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Votre abonnement</h3>
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
            {subscriptionInfo?.type}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="flex items-center space-x-3">
            <Award className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-medium">Statut</p>
              <p className={`text-sm ${subscriptionInfo?.status === 'Actif' ? 'text-green-600' : 'text-red-600'}`}>
                {subscriptionInfo?.status}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <CreditCard className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-medium">Crédits restants</p>
              <p className="text-sm text-indigo-600">
                {subscriptionInfo?.remainingCredits === 'Illimité' ? 'Illimité' : subscriptionInfo?.remainingCredits}
              </p>
            </div>
          </div>

          {subscriptionInfo?.expiresAt && (
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-indigo-600" />
              <div>
                <p className="font-medium">Expiration</p>
                <p className="text-sm text-gray-600">
                  {new Date(subscriptionInfo.expiresAt).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => setIsCallbackModalOpen(true)}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
        >
          <Phone className="w-5 h-5" />
          <span>Trouver plus de talents</span>
        </button>
      </div>

      {/* Logout Button */}
      <div className="flex justify-end">
        <button
          onClick={handleLogout}
          className="flex items-center space-x-2 text-red-600 hover:text-red-800 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Se déconnecter</span>
        </button>
      </div>

      {/* Callback Modal */}
      {isCallbackModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold gradient-text">Besoin d'aide pour vos recrutements ?</h3>
              <button 
                onClick={() => setIsCallbackModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Nous proposons une prestation innovante et adaptée à vos besoins en recrutement. 
              Laissez-nous votre numéro et nous vous rappellerons pour en discuter.
            </p>

            {success ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-green-600 font-medium">
                  Votre demande a été envoyée avec succès !
                </p>
              </div>
            ) : (
              <form onSubmit={handleCallbackRequest}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de téléphone
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="07 XX XX XX XX"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Phone className="w-4 h-4" />
                      <span>Rappelez-moi</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* LinkedIn Modal */}
      <LinkedInProfileModal 
        isOpen={isLinkedInModalOpen}
        onClose={() => setIsLinkedInModalOpen(false)}
      />
    </div>
  );
};

export default UserProfile;